import { useRef, useEffect, useState } from 'react';

const WOK_URL = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/lm6dnv3j_Cooking%20wok.png";

export const CraftSection = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = sectionRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      
      const visibleTop = Math.max(0, viewportHeight - rect.top);
      const progress = Math.min(1, Math.max(0, visibleTop / sectionHeight));
      
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      number: "01",
      title: "Tri-Ply Construction",
      description: "Three layers of metal bonded as one. SS316 interior for cooking, aluminium core for heat, SS403 exterior for durability.",
    },
    {
      number: "02",
      title: "Ergonomic Balance",
      description: "Weighted for comfort. Handles designed for hours of use without fatigue. Every gram considered.",
    },
    {
      number: "03",
      title: "Universal Compatibility",
      description: "From gas to induction, ceramic to halogen. Our cookware performs on any heat source you choose.",
    },
  ];

  return (
    <section
      ref={sectionRef}
      data-testid="craft-section"
      className="relative min-h-[150vh] bg-tex-bg"
    >
      {/* TECHNIQUE 1: Pinned Section */}
      <div className="sticky top-0 min-h-screen overflow-hidden flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            {/* Left: Text Content - TECHNIQUE 4: Left-aligned editorial */}
            <div>
              <p
                className="font-raleway text-[10px] font-semibold uppercase tracking-[0.5em] text-tex-violet-bright mb-6"
                style={{
                  opacity: Math.min(1, scrollProgress * 3),
                  transform: `translateY(${Math.max(0, 20 - scrollProgress * 60)}px)`
                }}
              >
                The Craft
              </p>

              <h2
                data-testid="craft-heading"
                className="text-editorial-section text-white text-left mb-12"
                style={{
                  opacity: Math.min(1, scrollProgress * 2.5),
                  transform: `translateY(${Math.max(0, 30 - scrollProgress * 90)}px)`
                }}
              >
                Engineering
                <br />
                that serves the cook.
              </h2>

              <div className="space-y-8">
                {features.map((feature, index) => (
                  <div
                    key={feature.number}
                    data-testid={`craft-feature-${index + 1}`}
                    className="flex gap-6"
                    style={{
                      opacity: Math.min(1, Math.max(0, (scrollProgress - 0.1 - index * 0.1) * 4)),
                      transform: `translateX(${Math.max(0, 30 - (scrollProgress - 0.1 - index * 0.1) * 120)}px)`
                    }}
                  >
                    <span className="font-mono text-xs text-tex-violet-bright/70 mt-1">
                      {feature.number}
                    </span>
                    <div>
                      <h3 className="font-raleway text-sm font-semibold uppercase tracking-wide text-tex-warm-white mb-2">
                        {feature.title}
                      </h3>
                      <p className="font-raleway text-sm font-light text-tex-muted leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div
                className="mt-12"
                style={{
                  opacity: Math.min(1, Math.max(0, (scrollProgress - 0.5) * 3))
                }}
              >
                <a
                  href="https://shop.texstyleco.com"
                  data-testid="craft-cta"
                  className="inline-flex items-center gap-3 font-raleway text-[11px] font-semibold uppercase tracking-[0.3em] text-tex-warm-white hover:text-tex-violet-bright transition-colors duration-300 group"
                >
                  Explore the Collection
                  <svg 
                    className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Right: Product Image - scroll-driven */}
            <div 
              className="relative"
              style={{
                opacity: Math.min(1, scrollProgress * 2),
                transform: `translateY(${50 - scrollProgress * 100}px) rotate(${scrollProgress * 5}deg)`
              }}
            >
              {/* Glow Effect */}
              <div 
                className="absolute inset-0 blur-3xl"
                style={{
                  background: `radial-gradient(circle, rgba(107, 68, 204, ${scrollProgress * 0.3}) 0%, transparent 70%)`
                }}
              />
              <img
                src={WOK_URL}
                alt="Tex-Style Company Premium Wok"
                data-testid="craft-image"
                className="relative z-10 w-full max-w-lg mx-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CraftSection;
